/**
 * Page change detection system for monitoring URL changes
 * Handles both SPA navigation and full page loads
 */
export class PageChangeDetector {
  private callbacks: ((url: string) => void)[] = [];
  private currentUrl = "";
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly debounceDelay: number;
  private isListening = false;
  private boundHandleUrlChange: () => void;

  constructor(debounceDelay = 300) {
    this.debounceDelay = debounceDelay;
    this.boundHandleUrlChange = this.handleUrlChange.bind(this);

    if (typeof window !== "undefined") {
      this.currentUrl = window.location.href;
    }
  }

  /**
   * Start listening for page changes
   */
  public startListening(): void {
    if (this.isListening || typeof window === "undefined") {
      return;
    }

    this.isListening = true;

    // Listen for browser back/forward navigation
    window.addEventListener("popstate", this.boundHandleUrlChange);

    // Listen for hash changes (for hash-based routing)
    window.addEventListener("hashchange", this.boundHandleUrlChange);

    // Monitor pushState and replaceState calls (SPA navigation)
    this.interceptHistoryMethods();

    console.log("PageChangeDetector: Started listening for page changes");
  }

  /**
   * Stop listening for page changes
   */
  public stopListening(): void {
    if (!this.isListening || typeof window === "undefined") {
      return;
    }

    this.isListening = false;

    // Remove event listeners
    window.removeEventListener("popstate", this.boundHandleUrlChange);
    window.removeEventListener("hashchange", this.boundHandleUrlChange);

    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    console.log("PageChangeDetector: Stopped listening for page changes");
  }

  /**
   * Add a callback to be called when URL changes
   */
  public addCallback(callback: (url: string) => void): void {
    this.callbacks.push(callback);
  }

  /**
   * Remove a callback
   */
  public removeCallback(callback: (url: string) => void): void {
    const index = this.callbacks.indexOf(callback);
    if (index > -1) {
      this.callbacks.splice(index, 1);
    }
  }

  /**
   * Get the current URL
   */
  public getCurrentUrl(): string {
    return this.currentUrl;
  }

  /**
   * Handle URL change events with debouncing
   */
  private handleUrlChange = (): void => {
    if (typeof window === "undefined") {
      return;
    }

    const newUrl = window.location.href;

    // Only process if URL actually changed
    if (newUrl === this.currentUrl) {
      return;
    }

    // Clear existing debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Debounce rapid URL changes
    this.debounceTimer = setTimeout(() => {
      this.currentUrl = newUrl;
      console.log("PageChangeDetector: URL changed to:", newUrl);

      // Notify all callbacks
      this.callbacks.forEach((callback) => {
        try {
          callback(newUrl);
        } catch (error) {
          console.error("Error in page change callback:", error);
        }
      });
    }, this.debounceDelay);
  };

  /**
   * Intercept pushState and replaceState calls to detect SPA navigation
   */
  private interceptHistoryMethods(): void {
    if (typeof window === "undefined") {
      return;
    }

    // Store original methods
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(
      window.history,
    );

    // Intercept pushState
    window.history.pushState = (...args) => {
      originalPushState(...args);
      this.boundHandleUrlChange();
    };

    // Intercept replaceState
    window.history.replaceState = (...args) => {
      originalReplaceState(...args);
      this.boundHandleUrlChange();
    };
  }
}
